import WebsitePauseConfirmation from "./WebsitePauseConfirmation";
import { WebsiteTrialDisclosure, useWebsiteTrialAcceptance, websiteStatusErrorMessage } from "./WebsiteTrialAcceptance";
import React, { useContext, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import Config from '../utils/Config';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePrompt } from "../hooks/usePrompt";

import ManageDealershipSelector from "./ManageDealershipSelector";
import SiteStatusPanel from "./SiteStatusPanel";
import DealershipInfoPanel from "./DealershipInfoPanel";
import DealerSitePanel from "./DealerSitePanel";
import DealershipOverviewPanel from "./DealershipOverviewPanel";
import DealerSpecialtiesPanel from "./DealerSpecialtiesPanel";
import SocialMediaPanel from "./SocialMediaPanel";
import DealerServicesPanel from "./DealerServicesPanel";
import CustomerTestimonialsPanel from "./CustomerTestimonialsPanel";
import MediaUploadPanel from "./MediaUploadPanel";
import FFLUploadPanel from "./FFLUploadPanel";
import DealerUploadFFL from "./DealerUploadFFL";
import BusinessHoursPanel from "./BusinessHoursPanel";
import PoliciesPanel from "./PoliciesPanel";
import {
  formatPhoneNumber,
  calculateExpirationDate,
  displayMessage,
} from '../utils/functions'; // Import utility functions
import Spinner from '../utils/spinner';
import "./Login.css";
import "./ClaimFFL.css";
import CropModal from "./CropModal";
import { toast } from "react-toastify";
import useTitle from '../hooks/useTitle';

const ClaimFFL = ({ fflData, onClaimSuccess, onUploadSuccess }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [switchingClaim, setSwitchingClaim] = useState(null);
  const [managementTab, setManagementTab] = useState(() => location.state?.managementTab === "website" ? "website" : "dealership");
  const [hydrated, setHydrated] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const draftAutosaveTimer = useRef(null);
  const latestSnapshot = useRef(null);
  const fflUploadRequest = useRef(null);
  const appliedSetupTarget = useRef(null);
  const [dealer, setDealer] = useState(null);
  const [fflNumber, setFflNumber] = useState(fflData?.fflNumber || "");
  const [expirationDate, setExpirationDate] = useState(null);
  const [fflName, setFflName] = useState(fflData?.fflName || "");
  const [businessName, setBusinessName] = useState(fflData?.businessName || "");
  const [email, setEmail] = useState(user?.email || "");
  const [status, setStatus] = useState("");
  const canCustomizeSite = hydrated && status === "Approved";
  const [siteStatus, setSiteStatus] = useState("Paused");
  const [website, setWebsite] = useState(fflData?.website || "");
  const [subdomain, setSubdomain] = useState(fflData?.subdomain || "");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [optionalPhoneNumber, setOptionalPhoneNumber] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [cityStateZip, setCityStateZip] = useState("");
  const [availability, setAvailability] = useState(null); // true / false / null
  const [checking, setChecking] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [isFFLReadOnly, setIsFFLReadOnly] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [deletedPhotos, setDeletedPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  useEffect(() => () => {
    if (filePreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(filePreviewUrl);
  }, [filePreviewUrl]);
  const [modalContentUrl, setModalContentUrl] = useState(null);
  const [modalContentType, setModalContentType] = useState("image");
  const fflInputRef = useRef(null); // ✅ Reference for FFL input field
  const fflNameRef = useRef(null);
  const fflBusinessNameRef = useRef(null);
  const [messageClass, setMessageClass] = useState("warning hidden"); // Default hidden
  const messageRef = useRef(null);
  const [themes, setThemes] = useState([]);
  const [originalSubdomain, setOriginalSubdomain] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [checkMessage, setCheckMessage] = useState("");   // holds reason from server
  const [testimonials, setTestimonials] = useState([
    { blurb: '', name: '', city: '', rating: 5 }
  ]);
  const [formData, setFormData] = useState({
    transferFee: '',
    ageGate: false,
    allowBlog: true,
    description: '',
    byAppointmentOnly: false,
    hours: {
      Monday: { open: '', close: '', closed: true },
      Tuesday: { open: '', close: '', closed: true },
      Wednesday: { open: '', close: '', closed: true },
      Thursday: { open: '', close: '', closed: true },
      Friday: { open: '', close: '', closed: true },
      Saturday: { open: '', close: '', closed: true },
      Sunday: { open: '', close: '', closed: true },
    },
    specialties: [],
    services: [],
    serviceDescriptions: {},
    bannerFile: null,
    bannerPreview: null,
    logoFile: null,
    logoPreview: null,
    policy_store: "",
    policy_shipping: "",
    policy_faq: ""
  });
  const [bannerCropSrc, setBannerCropSrc] = useState(null);
  const [photoCropSrc, setPhotoCropSrc] = useState(null);
  const [photoCropIndex, setPhotoCropIndex] = useState(null); // optional: to track which photo was clicked
  const fileInputRef = useRef();
  const [isDragOverBanner, setIsDragOverBanner] = useState(false);
  const [isDragOverLogo, setIsDragOverLogo] = useState(false);
  const [isDragOverPhoto, setIsDragOverPhoto] = useState(false);
  const [isDragOverFile, setIsDragOverFile] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [viewToken, setViewToken] = useState(null);
  const [showRenewModal, setShowRenewModal] = useState(null);
  const [bottomButtonsVisible, setBottomButtonsVisible] = useState(false);
  const logoDropInputRef = useRef(null);
  const bannerDropInputRef = useRef(null);
  const fflDropInputRef = useRef(null);
  const fflDropInputRenewRef = useRef(null);
  const formRef = useRef(null);
  const [customSpecialties, setCustomSpecialties] = useState([
    { title: '', description: '' }
  ]);
  const [scrollToRef, setScrollToRef] = useState(null);
  const [uploadedFflFile, setUploadedFflFile] = useState(null);
  const [allExpanded, setAllExpanded] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [panelStates, setPanelStates] = useState(() => {
    const saved = localStorage.getItem("claimFFLPanelStates");
    return saved
      ? JSON.parse(saved)
      : {
        siteStatus: false,
        dealershipInfo: true,
        dealerSite: false,
        dealerOverview: false,
        dealerType: false,
        socialMedia: false,
        services: false,
        testimonials: false,
        media: false,
        fflUpload: false,
        businessHours: false,
        policies: false,
      };
  });
  const emailRef = useRef(null);
  const subdomainRef = useRef(null);
  const subdomainInputRef = useRef(null);
  const themeRef = useRef(null);
  const fflFileRef = useRef(null);
  const specialtyRef = useRef(null);
  const businesshoursRef = useRef(null);
  const servicesRef = useRef(null);
  const bottomButtonsRef = useRef(null);

  useTitle('Edit FFL');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  let claimId = location.state?.claimId;
  const hideSiteStatusPanel =
    !status || /^(pending|cancelled|canceled)$/i.test(status.trim());

  const triggerSaveAnimation = () => {
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 1200);
  };

  const toggleAllSections = (expand) => {
    // 🔥 This is the only line you need to set the allExpanded state
    setAllExpanded(expand);

    // 🔁 Apply expand/collapse to each section in panelStates
    const updated = Object.keys(panelStates).reduce((acc, key) => {
      acc[key] = expand;
      return acc;
    }, {});

    setPanelStates(updated);
  };

  const submitViaCode = () => {
    if (!formRef.current) {
      console.error("⚠️ formRef is null—check the ref attribute.");
      return;
    }

    // Prefer modern API
    if (typeof formRef.current.requestSubmit === "function") {
      formRef.current.requestSubmit();
    } else {
      // Fallback for older browsers: fire a synthetic submit event
      const evt = new Event("submit", { bubbles: true, cancelable: true });
      formRef.current.dispatchEvent(evt);
    }
  };

  async function refreshPhotosFromServer(claimId, expectedSnapshot) {
    try {
      const res = await fetch(
        `${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_FETCH_PENDING_FFL_CLAIM}/${claimId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to refresh claim");
      if (expectedSnapshot !== undefined && latestSnapshot.current !== expectedSnapshot) return false;

      if (Array.isArray(data.photoPaths)) {
        setPhotos(
          data.photoPaths.map((path) => {
            const filename = path.split("/").pop();
            return {
              id: `server::${filename}`,
              url: `${Config.API.API_BASE_URL}${path}`,
              file: null,
              name: filename,
              size: 0,
              lastModified: 0,
              isNew: false,
              serverFilename: filename,
            };
          })
        );
      }
      if (expectedSnapshot !== undefined) {
        const baseline = JSON.parse(expectedSnapshot);
        if (Array.isArray(data.photoPaths)) baseline.photos = data.photoPaths.map(path => ({ name: path.split("/").pop(), file: null }));
        baseline.deletedPhotos = [];
        return JSON.stringify(baseline);
      }
      return true;
    } catch (e) {
      console.warn("Could not refresh photos:", e?.message || e);
      return false;
    }
  }

  if (!claimId || claimId === "undefined") {
    claimId = localStorage.getItem("claimId");
  }

  useEffect(() => {
    if (fflUploadRequest.current?.signal.aborted) {
      fflUploadRequest.current = null;
      setSubmitting(false);
    }
    return () => fflUploadRequest.current?.abort();
  }, [claimId, user?.id, user?._id]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];

    if (selectedFile && !allowedTypes.includes(selectedFile.type)) {
      alert("❌ Only PDF, PNG, and JPEG files are allowed.");
      return;
    }

    setFile(selectedFile);
    setUploadedFflFile(selectedFile);
    setFilePreviewUrl(URL.createObjectURL(selectedFile));
  };

  const handleDeletePhoto = (indexToRemove) => {
    const removed = photos[indexToRemove];
    // if server photo, queue the server filename for backend deletion
    if (!removed?.isNew && removed?.serverFilename) {
      setDeletedPhotos((prev) => Array.from(new Set([...prev, removed.serverFilename])));
    }
    // revoke blob for new files
    if (removed?.isNew && removed?.url?.startsWith('blob:')) {
      try { URL.revokeObjectURL(removed.url); } catch { }
    }

    const updatedPhotos = photos.filter((_, i) => i !== indexToRemove);
    const updatedPreviews = photoPreviews.filter((_, i) => i !== indexToRemove);
    setPhotos(updatedPhotos);
    setPhotoPreviews(updatedPreviews);
  };

  const openPhotoModal = (url, type = "image") => {
    setModalContentUrl(url);
    setModalContentType(type);
  };

  const closePhotoModal = () => {
    setModalContentUrl(null);
    setModalContentType("image");
  };

  const handleDroppedPhotos = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    files.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`❌ ${file.name} is not a valid image.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setPhotoCropSrc(reader.result);
        setPhotoCropIndex(null);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDroppedBanner = (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert("❌ Only JPG, PNG, or WEBP files allowed.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setBannerCropSrc(reader.result); // still opens crop modal
    };
    reader.readAsDataURL(file);
  };

  const handleDroppedLogo = (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert("❌ Only JPG, PNG, or WEBP files allowed.");
      return;
    }

    if (file.size > 512 * 1024) {
      toast.error("❌ Logo must be under 512 KB.");
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    setLogoFile(file);                // ✅ actually store the file
    setLogoPreview(previewUrl);      // ✅ trigger image preview
  };

  const handleDroppedFFL = async (file, claimId) => {
    if (!file) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("❌ Only PDF, JPG, or PNG files allowed.");
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    setFile(file); // 🔥 Sync actual file
    setFilePreviewUrl(previewUrl); // 🔥 Add preview for modal

    setFormData((prev) => ({
      ...prev,
      fflFile: file,
      fflFileName: file.name,
    }));

    fflUploadRequest.current?.abort();
    const controller = new AbortController();
    fflUploadRequest.current = controller;
    setSubmitting(true);
    const isCurrent = () => fflUploadRequest.current === controller && !controller.signal.aborted;
    let confirmed = false;
    try {
      const formData = new FormData();
      formData.append("fflFile", file);
      formData.append("claimId", claimId);

      const res = await fetch(Config.API.API_BASE_URL + Config.ENDPOINTS.DEALERS_UPLOAD_RENEWAL, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + localStorage.getItem("authToken"),
        },
        body: formData,
        signal: controller.signal,
      });
      const data = await res.json().catch(() => null);
      if (!isCurrent()) return;
      if (!res.ok) {
        const serverMessage = typeof data?.message === "string" && data.message.trim() && data.message.length <= 300
          ? data.message.trim() : null;
        const fallback = res.status === 401 ? "Please sign in again before uploading your FFL."
          : res.status === 403 ? "You do not have permission to upload this document."
          : "FFL upload failed. Your selected document is still available. Please try again.";
        toast.error(serverMessage || fallback);
        return;
      }
      if (data?.message !== "Renewal uploaded successfully") {
        toast.error("The upload could not be confirmed. Your selected document is still available. Check the claim before retrying.");
        return;
      }
      confirmed = true;
      setShowRenewModal(false);
      toast.success("✅ FFL uploaded. Pending approval.");
    } catch {
      if (isCurrent()) toast.error("The upload could not be confirmed. Your selected document is still available. Check your connection and the claim before retrying.");
    } finally {
      if (isCurrent()) {
        if (!confirmed) {
          // Retain the selected File in React state, but permit choosing it again.
          if (fflDropInputRef.current) fflDropInputRef.current.value = "";
          if (fflDropInputRenewRef.current) fflDropInputRenewRef.current.value = "";
        }
        fflUploadRequest.current = null;
        setSubmitting(false);
      }
    }
    if (confirmed && onUploadSuccess) onUploadSuccess();
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const updateLocalStorage = (updatedFields) => {
    const existing = JSON.parse(localStorage.getItem("claimFFLData") || "{}");
    localStorage.setItem("claimFFLData", JSON.stringify({ ...existing, ...updatedFields }));
  };

  const updateTestimonial = (i, field, val) =>
    setTestimonials(ts => ts.map((t, idx) => idx === i ? { ...t, [field]: val } : t));

  const addTestimonial = () =>
    setTestimonials(ts => [...ts, { blurb: '', name: '', city: '', rating: 5 }]);

  const removeTestimonial = i =>
    setTestimonials(ts => ts.filter((_, idx) => idx !== i));

  const clearAllFields = () => {
    setFflNumber("");
    setFflName("");
    setBusinessName("");
    setEmail("");
    setWebsite("");
    setFile(null);
    setSubdomain("");
    setPhoneNumber("");
    setOptionalPhoneNumber("");
    setStreetAddress("");
    setCityStateZip("");
    setExpirationDate("");

    setFormData({
      transferFee: '',
      ageGate: false,
      allowBlog: true,
      description: '',
    byAppointmentOnly: false,
      policy_store: "",
      policy_shipping: "",
      policy_faq: "",
      hours: {
        Monday: { open: '', close: '', closed: true },
        Tuesday: { open: '', close: '', closed: true },
        Wednesday: { open: '', close: '', closed: true },
        Thursday: { open: '', close: '', closed: true },
        Friday: { open: '', close: '', closed: true },
        Saturday: { open: '', close: '', closed: true },
        Sunday: { open: '', close: '', closed: true },
      },
    });

    localStorage.removeItem("claimFFLData");
    messageRef.current.innerHTML = "";
    messageRef.current.style.display = "none";
    fflInputRef.current?.focus(); // Focus on FFL Number input
  };

  const displayWarning = (msg) => {
    toast.error(`❌ ${msg}`);
    // displayMessage(messageRef, `❌ ${msg}`, 'warning', setMessageClass);
  };

  const expandPanelAndScroll = (panelKey, fieldRef) => {
    setManagementTab(["dealershipInfo", "fflUpload"].includes(panelKey) ? "dealership" : "website");
    setPanelStates(prev => ({ ...prev, [panelKey]: true }));
    setTimeout(() => {
      fieldRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      fieldRef?.current?.focus();
    }, 300); // match CollapsibleSection animation time
  };

  const validateForm = () => {
    const rawFFL = fflNumber.replace(/-/g, "");

    if (rawFFL.length !== 15) {
      expandPanelAndScroll('dealershipInfo', fflInputRef);
      return displayWarning("FFL Number must be exactly 15 characters.");
    }

    if (!/^[A-Z0-9]+$/.test(rawFFL)) {
      expandPanelAndScroll('dealershipInfo', fflInputRef);
      return displayWarning("FFL Number must contain only letters and numbers.");
    }

    if (!fflName.trim()) {
      expandPanelAndScroll('dealershipInfo', fflNameRef);
      return displayWarning("FFL Name is required.");
    }

    if (!email.trim()) {
      setManagementTab("dealership");
      setPanelStates(prev => ({ ...prev, dealershipInfo: true }));
      setScrollToRef(() => emailRef);
      return displayWarning("Email is required.");
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      expandPanelAndScroll('dealershipInfo');
      return displayWarning("Please enter a valid email address.");
    }
    const publicPhone = optionalPhoneNumber.trim();
    const publicPhoneDigits = publicPhone.replace(/\D/g, '');
    if (publicPhone && (!/^\+?[\d\s().-]+$/.test(publicPhone) || publicPhoneDigits.length < 10 || publicPhoneDigits.length > 15)) {
      expandPanelAndScroll('dealershipInfo');
      return displayWarning("Please enter a valid optional dealer contact phone number.");
    }
    if (!canCustomizeSite) {
      if (!file) {
        expandPanelAndScroll('fflUpload', fflFileRef);
        return displayWarning("Please upload a copy of your FFL. Files must be selected again when restoring a draft.");
      }
      return true;
    }

    const overview = (formData.description || '').trim().replace(/\s+/gu, ' ');
    if (overview.length < 100 || overview.length > 300 || !/[\p{L}\p{N}]/u.test(overview)) {
      expandPanelAndScroll('dealerOverview', { current: document.getElementById('description') });
      return displayWarning("Dealership Overview is required. Enter 100–300 meaningful characters before saving. Extra whitespace does not count.");
    }

    if (!subdomain) {
      setManagementTab("website");
      setValidationErrors(prev => ({ ...prev, subdomain: true }));
      subdomainInputRef.current?.focus();
      setPanelStates(prev => ({ ...prev, dealerSite: true }));

      // 👇 set scroll target and force focus after delay
      setScrollToRef(() => {
        setTimeout(() => {
          subdomainRef.current?.focus();
        }, 0);
        return subdomainRef;
      });

      return displayWarning("Please choose your web address.");
    }

    if (!formData.theme) {
      setManagementTab("website");
      setValidationErrors(prev => ({ ...prev, theme: true }));
      setPanelStates(prev => ({ ...prev, dealerSite: true }));
      setScrollToRef(themeRef); // optional if you want to scroll to it
      return displayWarning("Please select a theme.");
    }

    const hasBuiltIn = Array.isArray(formData.specialties) && formData.specialties.length > 0;
    const hasCustom = customSpecialties.some(s => s.title?.trim() && s.description?.trim());

    if (!hasBuiltIn && !hasCustom) {
      setManagementTab("website");
      setValidationErrors(prev => ({ ...prev, dealerType: true }));
      setPanelStates(prev => ({ ...prev, dealerType: true }));
      setScrollToRef(specialtyRef);
      return displayWarning("Please choose at least one specialty.");
    }

    const atLeastOneOpen = Object.values(formData.hours).some(day => !day.closed);
    if (!formData.byAppointmentOnly && !atLeastOneOpen) {
      setManagementTab("website");
      setValidationErrors(prev => ({ ...prev, businessHours: true }));
      setPanelStates(prev => ({ ...prev, businessHours: true }));
      setScrollToRef(businesshoursRef);
      return displayWarning("Please open at least one day of the week.");
    }

    setValidationErrors(prev => ({ ...prev, subdomain: false }));


    return true;
  };

  const handleFFLChange = (event) => {
    let value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""); // Remove non-alphanumeric characters

    // Ensure 10th character must be a letter
    if (value.length >= 10 && !/^[A-Z]$/.test(value[9])) {
      return; // Stops typing if the 10th character is not a letter
    }

    // Apply correct FFL formatting: X-XX-XXX-XX-XA-XXXXX
    if (value.length > 1) value = value.replace(/^(.{1})(.)/, "$1-$2"); // After 1st character
    if (value.length > 4) value = value.replace(/^(.{1}-.{2})(.)/, "$1-$2"); // After 3rd character
    if (value.length > 7) value = value.replace(/^(.{1}-.{2}-.{3})(.)/, "$1-$2"); // After 6th character
    if (value.length > 9) value = value.replace(/^(.{1}-.{2}-.{3}-.{2})(.)/, "$1-$2"); // After 8th character
    if (value.length > 12) value = value.replace(/^(.{1}-.{2}-.{3}-.{2}-.{2})(.)/, "$1-$2"); // After 10th character
    if (value.length > 18) value = value.replace(/^(.{1}-.{2}-.{3}-.{2}-.{2}-.{5})(.)/, "$1-$2"); // After 15th character

    // Prevent more than 15 total alphanumeric characters (excluding hyphens)
    if (value.replace(/-/g, "").length > 15) {
      return;
    }

    setFflNumber(value);
    updateLocalStorage({ fflNumber: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;            // safety
    if (!hydrated) {
      toast.error("Please wait for dealership information to finish loading, or reload if loading failed.");
      return;
    }

    if (!validateForm()) return;
    const submittedSnapshot = editorSnapshot;

    setSubmitting(true);               // 🔒 disable buttons

    const userId = user?.id || "";
    const userEmail = user?.email || "";
    const userFullName = `${user?.firstName} ${user?.lastName}`;

    const payload = new FormData(); // ✅ renamed
    payload.append("fflNumber", fflNumber);
    payload.append("fflName", fflName);
    payload.append("businessName", businessName);
    payload.append("claimId", claimId);
    if (file instanceof File && !canCustomizeSite) payload.append("fflFile", file);
    payload.append("userEmail", userEmail);
    payload.append("userId", userId);
    payload.append("userFullName", userFullName);
    payload.append("email", email);
    payload.append("website", website);
    payload.append("phoneNumber", phoneNumber);
    payload.append("optionalPhoneNumber", optionalPhoneNumber);
    payload.append("address", streetAddress);
    payload.append("cityStateZip", cityStateZip);
    payload.append("expirationDate", expirationDate);
    payload.append("subdomain", subdomain);
    payload.append("siteStatus", siteStatus);
    payload.append("transferFee", formData.transferFee || "0");
    payload.append("ageGate", formData.ageGate ? "true" : "false");
    payload.append("allowBlog", "true");
    payload.append("description", formData.description || "");
    payload.append("byAppointmentOnly", String(formData.byAppointmentOnly === true));
    payload.append("businessHours", JSON.stringify(formData.hours)); // ✅ now works
    payload.append("instagram", formData.instagram || "");
    payload.append("facebook", formData.facebook || "");
    payload.append("youtube", formData.youtube || "");
    payload.append("policy_store", formData.policy_store || "");
    payload.append("policy_shipping", formData.policy_shipping || "");
    payload.append("policy_faq", formData.policy_faq || "");
    payload.append("theme", formData.theme || "modern-steel");
    payload.append('testimonials', JSON.stringify(testimonials.filter(t => t.blurb)));
    payload.append("customSpecialties", JSON.stringify(customSpecialties.filter(s => s.title && s.description)));

    // 2) deleted server filenames (append each as its own field)
    deletedPhotos.forEach(name => payload.append('deletedPhotos', name));

    formData.specialties.forEach((type) => {
      payload.append("specialties", type);
    });

    formData.services.forEach((service) => {
      payload.append("services", service);
    });

    payload.append(
      "serviceDescriptions",
      JSON.stringify(formData.serviceDescriptions || {})
    );

    if (formData.bannerFile instanceof File) {
      payload.append("banner", formData.bannerFile);
    }

    if (logoFile instanceof File) {
      payload.append("logo", logoFile);
    }

    if (photos.length > Config.APP.MAX_PHOTO_UPLOADS) {
      displayWarning(`Only ${Config.APP.MAX_PHOTO_UPLOADS} photos allowed. Please remove extras before submitting.`);
      return;
    }

    // 🟢 Upload new photo files only
    photos.forEach((photo) => {
      if (photo.isNew && photo.file instanceof File) {
        payload.append("photos", photo.file);
      }
    });

    if (!canCustomizeSite) {
      const essentialFields = new Set(["fflNumber", "fflName", "businessName", "claimId", "fflFile", "userFullName", "email", "phoneNumber", "optionalPhoneNumber", "website", "address", "cityStateZip", "expirationDate"]);
      for (const key of [...payload.keys()]) if (!essentialFields.has(key)) payload.delete(key);
    }
    try {
      const response = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_CLAIM_FFL}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("authToken")}`, // Send user token
        },
        body: payload,
      });

      const data = await response.json();
      if (response.ok) {
        // Clear only this claim's submitted draft; newer edits remain dirty and recoverable.
        if (latestSnapshot.current === submittedSnapshot) {
          clearTimeout(draftAutosaveTimer.current);
          try {
            const draft = JSON.parse(localStorage.getItem("claimFFLData") || "null");
            const matchesClaim = draft?.claimId ? String(draft.claimId) === String(claimId)
              : draft?.fflNumber?.replace(/-/g, "") === fflNumber.replace(/-/g, "");
            if (matchesClaim) localStorage.removeItem("claimFFLData");
          } catch { /* Browser storage failure does not undo a confirmed server save. */ }
          setLastSaved(null);
        }
        setSavedSnapshot(submittedSnapshot);
        if (data.claimId) {
          localStorage.setItem("claimId", data.claimId);
          claimId = data.claimId;
        }
        if (data.status) setStatus(data.status);
        if (canCustomizeSite) setOriginalSubdomain(subdomain);
        toast.success("Your data has been saved.", { autoClose: 1000 });
        displayMessage(messageRef, "", 'warning', setMessageClass);
        setMessageClass('clear');
        triggerSaveAnimation();

        // 🔄 make client photo state match server and clear deletion queue
        const refreshed = claimId ? await refreshPhotosFromServer(claimId, submittedSnapshot) : submittedSnapshot;
        if (refreshed) {
          setDeletedPhotos([]);
          setSavedSnapshot(refreshed);
        } else {
          setSavedSnapshot(submittedSnapshot);
        }

        if (typeof onClaimSuccess === "function") {
          onClaimSuccess();
        }
      } else {
        toast.error(`❌ ${data.message}`);
        displayMessage(messageRef, `❌ ${data.message}`, 'warning', setMessageClass);
      }
    } catch (error) {
      toast.error("❌ Error submitting FFL update.");
    } finally {
      setSubmitting(false);            // 🔓 re-enable buttons
    }
  };

  const handleGenerateAI = async () => {
    setLoadingAI(true);
    try {
      const response = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.AI_GENERATE_DESCRIPTION}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          fflName,
          businessName,
          licenseName: fflName,
          transferFee: formData.transferFee,
          services: {
            nfaSupport: formData.nfaSupport,
            fingerprinting: formData.fingerprinting,
            appointments: formData.appointments,
          },
          rawDescription: formData.description,
        }),
      });

      const data = await response.json();
      if (response.ok && data.description) {
        setFormData(prev => ({
          ...prev,
          description: data.description.slice(0, 300)
        }));
      } else {
        alert("❌ Failed to generate AI description.");
      }
    } catch (err) {
      console.error("AI description error:", err);
      alert("❌ Error talking to AI.");
    } finally {
      setLoadingAI(false);
    }
  };

  // assume: const [siteStatus, setSiteStatus] = useState("Paused");

  const trial = useWebsiteTrialAcceptance(claimId);
  const [pauseRequested, setPauseRequested] = useState(false);
  const [siteStatusPending, setSiteStatusPending] = useState(false);
  const siteStatusRequest = useRef(false);
  const requestSiteStatus = (next) => {
    if (!canCustomizeSite || siteStatusRequest.current || next === siteStatus) return;
    if (siteStatus === "Published" && next === "Paused") setPauseRequested(true);
    else return handleSiteStatus(next);
  };
  const handleSiteStatus = async (next) => {
    if (!canCustomizeSite || siteStatusRequest.current || next === siteStatus) return;
    siteStatusRequest.current = true;
    setSiteStatusPending(true);
    // next is "Published" or "Paused"
    const prev = siteStatus;
    const isActive = next === "Published";

    try {
      const permission = isActive ? await trial.request(claimId) : {allowed:true};
      if (!permission.allowed) return;
      const res = await fetch(
        `${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_TOGGLE_SITE_STATUS}/${claimId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({ isActive, trialAcceptance: permission.acceptance }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw Object.assign(new Error("Status rejected"), {publicMessage:websiteStatusErrorMessage(data, res.status)});
      if (data?.siteStatus !== next) throw Object.assign(new Error("Unconfirmed status"), {publicMessage:"The website status change could not be confirmed. Refresh before trying again."});

      setSiteStatus(next);
      toast.success(`✅ Updated site status to ${next}.`);
    } catch (err) {
      // rollback on error
      setSiteStatus(prev);
      console.error(err);
      toast.error(err.publicMessage || "Couldn't update site status. Please try again.");
    } finally {
      siteStatusRequest.current = false;
      setSiteStatusPending(false);
    }
  };

  const viewWebsite = () => {
    if (!canCustomizeSite) return;
    if (subdomain && subdomain !== "yourname" && Config.API.WEBSITE_BASE_URL) {
      const dealerURL = status === "Approved" && siteStatus === "Published"
        ? Config.API.WEBSITE_BASE_URL.replace("<sitename>", subdomain)
        : claimId ? new URL('/website-preview/' + encodeURIComponent(claimId), window.location.origin).href : null;
      if (dealerURL) window.open(dealerURL, "_blank", "noopener,noreferrer");
    } else {
      console.warn("Invalid or missing subdomain.");
    }
  };

  const addSpecialty = () => {
    setCustomSpecialties([...customSpecialties, { title: '', description: '' }]);
  };

  const removeSpecialty = (index) => {
    const updated = [...customSpecialties];
    updated.splice(index, 1);
    setCustomSpecialties(updated);
  };

  const updateSpecialty = (index, field, value) => {
    const updated = [...customSpecialties];
    updated[index][field] = value;
    setCustomSpecialties(updated);
  };

  const normalizeURL = (url) => {
    if (!url) return "";
    return url.startsWith("http://") || url.startsWith("https://")
      ? url
      : `https://${url}`;
  };

  const dealerId = localStorage.getItem("dealerId");
  useEffect(() => {
    if (claimId) return; // Existing claims hydrate only from their authorized claim endpoint.
    const fetchDealer = async () => {
      setHydrated(false);
      setSavedSnapshot(null);
      try {
      const response = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_DETAILS}/${dealerId}`, {
        method: 'GET'
      });
      const data = await response.json();
      if (!response.ok) throw new Error("Unable to load dealership information.");
      if (data) {
        setPhoneNumber(data.VOICE_PHONE ? formatPhoneNumber(data.VOICE_PHONE) : "")
        setStreetAddress(data.PREMISE_ADDRESS?.STREET || "")
        setCityStateZip([data.PREMISE_ADDRESS?.CITY, [data.PREMISE_ADDRESS?.STATE, data.PREMISE_ADDRESS?.ZIP_CODE].filter(Boolean).join(" ")].filter(Boolean).join(", "))
        setEmail(localStorage.getItem("userEmail") || "");
        setWebsite(data.WEBSITE || "");
        setSubdomain(data.SUBDOMAIN || "");
        setExpirationDate(calculateExpirationDate(data.LIC_XPRDTE));
      }
      setHydrated(true);
      } catch (error) {
        toast.error("Unable to load dealership information. Please reload.");
      }
    }

    if (dealerId) {
      fetchDealer();
    } else {
      setHydrated(true);
    }
  }, [dealerId]);

  // ✅ Close the upload modal
  const closeUploadModal = () => {
    setShowRenewModal(false);
    localStorage.removeItem("showRenewModal");
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomButtonsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 } // Trigger when at least 10% of the row is visible
    );

    if (bottomButtonsRef.current) {
      observer.observe(bottomButtonsRef.current);
    }

    return () => {
      if (bottomButtonsRef.current) {
        observer.unobserve(bottomButtonsRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const hasServerRecord = Boolean(claimId);
    if (hasServerRecord) return; // prefer server values

    const raw = localStorage.getItem("claimFFLData");
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (fflData?.fflNumber && parsed.fflNumber?.replace(/-/g, "") !== fflData.fflNumber.replace(/-/g, "")) return;
      setFflNumber(parsed.fflNumber || "");
      setFflName(parsed.fflName || "");
      setBusinessName(parsed.businessName || "");
      setEmail(parsed.email || "");
      setWebsite(parsed.website || "");
      setSubdomain(parsed.subdomain || "");
      setSiteStatus(parsed.siteStatus || "");
      setPhoneNumber(parsed.phoneNumber || "");
      setOptionalPhoneNumber(parsed.optionalPhoneNumber || "");
      setStreetAddress(parsed.streetAddress || "");
      setCityStateZip(parsed.cityStateZip || "");
      setExpirationDate(parsed.expirationDate || "");
      setFormData(prev => ({
        ...prev,
        description: parsed.description || "",
        transferFee: parsed.transferFee || "0",
        ageGate: parsed.ageGate || false,
        allowBlog: true,
        policy_store: parsed.policy_store || "",
        policy_shipping: parsed.policy_shipping || "",
        policy_faq: parsed.policy_faq || "",
        specialties: parsed.specialties || [],
        services: parsed.services || [],
        instagram: parsed.instagram || "",
        facebook: parsed.facebook || "",
        youtube: parsed.youtube || "",
        hours: parsed.hours || prev.hours,
        byAppointmentOnly: parsed.byAppointmentOnly === true,
        theme: parsed.theme || prev.theme,
      }));
    } catch (e) {
      console.warn("Failed to parse saved draft:", e);
    }
  }, [claimId, fflData]);

  useEffect(() => {
    return () => {
      photoPreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [photoPreviews]);

  useEffect(() => {
    if (claimId) return; // Existing-claim navigation must not relabel another claim's local draft.
    if (fflData?.fflNumber) {
      updateLocalStorage({ fflNumber: fflData?.fflNumber });
    }
    if (fflData?.fflName) {
      updateLocalStorage({ fflName: fflData?.fflName });
    }
    if (fflData?.businessName) {
      updateLocalStorage({ businessName: fflData?.businessName });
    }
    if (fflData?.website) {
      updateLocalStorage({ website: fflData?.website });
    }
  }, [user]);

  useEffect(() => {
    const cameFromClaim = localStorage.getItem("fflClaimedOnce") === "true" && !!fflData?.fflNumber;
    if (cameFromClaim) {
      setIsFFLReadOnly(true);
    }
    if (filePreviewUrl) {
      setUploadedFflFile(true);
    }

  }, []);

  const editorSnapshot = JSON.stringify({
    fflNumber, fflName, businessName, email, website, phoneNumber, optionalPhoneNumber,
    streetAddress, cityStateZip, expirationDate, subdomain, siteStatus,
    formData: { ...formData, bannerPreview: undefined, logoPreview: undefined },
    file, logoFile, photos: photos.map(photo => ({ name: photo.name, file: photo.file })),
    deletedPhotos, testimonials, customSpecialties,
  }, (key, value) => value instanceof File ? { name: value.name, size: value.size, type: value.type, lastModified: value.lastModified } : value);
  latestSnapshot.current = editorSnapshot;
  useEffect(() => {
    if (hydrated && savedSnapshot === null) {
      setSavedSnapshot(editorSnapshot);
    }
  }, [hydrated, editorSnapshot, savedSnapshot]);
  const hasUnsavedChanges = hydrated && savedSnapshot !== null && editorSnapshot !== savedSnapshot;
  usePrompt("You have unsaved changes. Are you sure you want to leave?", hasUnsavedChanges && !switchingClaim);
  // Navigate after usePrompt has removed its guard for this explicitly confirmed switch.
  useEffect(() => {
    if (!switchingClaim) return;
    localStorage.removeItem("fflClaimedOnce");
    localStorage.setItem("fflEditData", true);
    localStorage.setItem("claimId", switchingClaim._id);
    localStorage.setItem("dealerId", switchingClaim.dealerId);
    navigate("/dashboard", { state: {
      activeTab: "claim-ffl", managementTab, claimId: switchingClaim._id, dealerId: switchingClaim.dealerId,
      fflNumber: switchingClaim.fflNumber, fflName: switchingClaim.fflName,
    } });
  }, [switchingClaim, navigate, managementTab]);


  useEffect(() => {
    if (claimId && (!hydrated || !hasUnsavedChanges)) {
      setLastSaved(null);
      return;
    }
    const timeout = setTimeout(() => {
      const autoSaveFields = {
        fflNumber,
        fflName,
        businessName,
        email,
        website,
        subdomain,
        siteStatus,
        optionalPhoneNumber,
        phoneNumber,
        streetAddress,
        cityStateZip,
        expirationDate,
        theme: formData.theme,
        description: formData.description,
        transferFee: formData.transferFee,
        ageGate: formData.ageGate,
        allowBlog: formData.allowBlog,
        policy_store: formData.policy_store,
        policy_shipping: formData.policy_shipping,
        policy_faq: formData.policy_faq,
        specialties: formData.specialties,
        services: formData.services,
        instagram: formData.instagram,
        facebook: formData.facebook,
        youtube: formData.youtube,
        hours: formData.hours,
        byAppointmentOnly: formData.byAppointmentOnly === true,
        services: formData.services,
      };

      const savedFields = canCustomizeSite ? autoSaveFields : {
        fflNumber, fflName, businessName, email, phoneNumber, optionalPhoneNumber, streetAddress, cityStateZip, expirationDate,
      };
      try {
        localStorage.setItem("claimFFLData", JSON.stringify({ ...savedFields, claimId }));
        setLastSaved(new Date());
      } catch {
        setLastSaved(null);
      }
    }, 500); // debounce
    draftAutosaveTimer.current = timeout;

    return () => clearTimeout(timeout);
  }, [
    claimId, hydrated, hasUnsavedChanges, editorSnapshot,
    fflNumber,
    fflName,
    businessName,
    email,
    website,
    subdomain,
    siteStatus,
    optionalPhoneNumber,
    phoneNumber, streetAddress, cityStateZip, expirationDate, canCustomizeSite,
    formData
  ]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!hasUnsavedChanges) return;

      e.preventDefault();
      e.returnValue = ""; // Required for modern browsers
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    return () => {
      photos.forEach(photo => {
        if (photo.file && photo.url) URL.revokeObjectURL(photo.url);
      });
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
  }, []);

  useEffect(() => {
    // ✅ skip availability check if it's their own subdomain
    if (originalSubdomain && subdomain === originalSubdomain) {
      setAvailability(true);
      return;
    }

    if (!subdomain || subdomain.length < 3) {
      setAvailability(null); // Or false/null to disable UI check
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        setChecking(true);
        const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_CHECK_SUBDOMAIN}/${subdomain}`, {
          signal: controller.signal,
        });
        const data = await res.json();
        setCheckMessage(data.reason);
        setAvailability(data.available);
      } catch (err) {
        if (err.name !== "AbortError") console.error("Subdomain check error:", err);
      } finally {
        setChecking(false);
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [subdomain, originalSubdomain]);

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_FETCH_ACTIVE_TEMPLATE}`);
        const data = await res.json();
        setThemes(data);
      } catch (err) {
        console.error("❌ Failed to fetch themes:", err);
      }
    };
    fetchThemes();
  }, []);

  useEffect(() => {
    if (!claimId) return;
    setHydrated(false);
    setSavedSnapshot(null);

    const dealerId = localStorage.getItem("dealerId");

    const fetchClaim = async () => {
      const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_FETCH_PENDING_FFL_CLAIM}/${claimId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      const data = await res.json();

      const bannerUrl = data.bannerPath
        ? `${Config.API.API_BASE_URL}${data.bannerPath}`
        : null;
      const bannerFileName = data.bannerPath
        ? data.bannerPath.split("/").pop()
        : null;
      /* ---- NEW: existing logo, if any ---- */
      const logoUrl = data.logoPath
        ? `${Config.API.API_BASE_URL}${data.logoPath}`
        : null;
      setStatus(`${data.status}`);
      setSiteStatus(`${data.siteStatus}`);

      if (!res.ok) throw new Error(data.message);

      setViewToken(data.viewToken || "");
      setFflNumber(data.fflNumber || "");
      setFflName(data.fflName || "");
      setBusinessName(data.businessName || "");
      setWebsite(data.website || "");
      setPhoneNumber(formatPhoneNumber(data.phone) || "");
      setOptionalPhoneNumber(data.optional_phone && data.optional_phone !== "N/A" ? formatPhoneNumber(data.optional_phone) : "");
      setEmail(data.email || "");
      setStreetAddress(data.street)
      setCityStateZip(`${data.cityStateZip}`)
      setExpirationDate(`${data.expirationDate}`)

      if (data.filePath) {
        const fileName = data.filePath.split("/").pop();
        const fetchedFile = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_DOWNLOAD_FFL}/${claimId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        });
        if (fetchedFile.status === 404) {
          setFile(null);
          setFilePreviewUrl(null);
          // Approved claims retain their verification independently of this historical attachment.
          if (data.status !== "Approved") {
            toast.warn("Your previously uploaded FFL document is unavailable. Please upload a replacement.");
          }
        } else {
          if (!fetchedFile.ok) throw new Error("Unable to retrieve FFL document.");
          const blob = await fetchedFile.blob();
          const fileFromBlob = new File([blob], fileName, { type: blob.type });
          setFile(fileFromBlob);
          setFilePreviewUrl(URL.createObjectURL(blob));
        }
      }
      setSubdomain(data.subdomain || "");
      setOriginalSubdomain(data.subdomain || null);
      setFormData(prev => ({
        ...prev,
        description: data.description || "",
        policy_store: data.policy_store || "",
        policy_shipping: data.policy_shipping || "",
        policy_faq: data.policy_faq || "",
        transferFee: data.transferFee?.toString() || "",
        ageGate: !!data.ageGate,
        allowBlog: true,
        hours: data.businessHours || prev.hours,
        byAppointmentOnly: data.byAppointmentOnly === true,
        instagram: data.instagram || "",
        facebook: data.facebook || "",
        youtube: data.youtube || "",
        bannerPreview: bannerUrl, // ✅ this line is key
        bannerFile: null, // ✅ explicitly null to avoid conflicts
        logoPreview: logoUrl, // ✅ this line is key
        logoFile: null, // ✅ explicitly null to avoid conflicts
        bannerFileName: bannerFileName, // ✅ ADD THIS LINE
        specialties: data.specialties,
        serviceDescriptions: data.serviceDescriptions || {},
        services: data.services || [],
        theme: data.theme,
      }));

      // Stored assets are previews, not newly selected uploads (including missing/error URLs).
      setLogoFile(null);
      setLogoPreview(logoUrl);

      if (Array.isArray(data.photoPaths)) {
        // existing photos from server: not "file" uploads; mark isNew=false
        setPhotos(
          data.photoPaths.map((path) => {
            const filename = path.split("/").pop();
            return {
              id: `server::${filename}`,
              url: `${Config.API.API_BASE_URL}${path}`,
              file: null,
              name: filename,
              size: 0,
              lastModified: 0,
              isNew: false,
              serverFilename: filename,
            };
          })
        );
      }

      if (Array.isArray(data.testimonials)) {
        setTestimonials(data.testimonials);
      }

      if (Array.isArray(data.customSpecialties)) {
        setCustomSpecialties(data.customSpecialties);
      }

      setIsFFLReadOnly(true);
      setHydrated(true);
    };

    fetchClaim().catch(() => toast.error("Unable to load the full claim. Please reload before saving."));
  }, [claimId]);

  useEffect(() => {
    if (hydrated && !canCustomizeSite && managementTab === "website") setManagementTab("dealership");
  }, [hydrated, canCustomizeSite, managementTab]);

  // Apply a Dashboard checklist target once, after the authorized claim is loaded.
  useEffect(() => {
    const section = location.state?.setupSection;
    if (!hydrated || status !== "Approved" || String(location.state?.claimId) !== String(claimId) ||
        !["fflUpload", "dealerSite", "dealerType", "businessHours", "siteStatus", "dealershipInfo"].includes(section)) return;
    const targetKey = String(location.key) + ":" + claimId + ":" + section;
    if (appliedSetupTarget.current === targetKey) return;
    appliedSetupTarget.current = targetKey;
    setManagementTab(["fflUpload", "dealershipInfo"].includes(section) ? "dealership" : "website");
    setPanelStates(previous => ({ ...previous, [section]: true }));
    if (section === "dealerSite") setScrollToRef(subdomainRef);
  }, [hydrated, status, claimId, location.key, location.state?.claimId, location.state?.setupSection]);

  let dealerURL = null;
  if (
    subdomain &&
    subdomain !== "yourname" &&
    Config.API.WEBSITE_BASE_URL &&
    Config.API.WEBSITE_BASE_URL.includes("<sitename>")
  ) {
    dealerURL = Config.API.WEBSITE_BASE_URL.replace("<sitename>", subdomain);
  }

  useEffect(() => {
    if (subdomain && availability === true && !checkMessage) {
      setValidationErrors(prev => ({ ...prev, subdomain: false }));
    }
  }, [availability, checkMessage, subdomain]);

  // Save panelStates to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("claimFFLPanelStates", JSON.stringify(panelStates));
  }, [panelStates]);

  useEffect(() => {
    if (localStorage.getItem("showRenewModal")) {
      setShowRenewModal(true);
      localStorage.removeItem("showRenewModal");
    }
  }, [claimId]);

  // helper to build client objects for server photos
  function preloadExisting(photoPaths = []) {
    return photoPaths.map((p) => {
      const filename = p.split('/').pop();
      return {
        id: `server::${filename}`, // unique id for ReactSortable
        url: p,                    // server URL, e.g. "/uploads/Images/xyz.jpg"
        file: null,                // no blob for server-side photos
        name: filename,
        size: 0,
        lastModified: 0,
        isNew: false,              // <-- mark EXISTING
        serverFilename: filename,  // keep bare filename for server ops
      };
    });
  }

  return (
    <div className="login-container login-container-dashboard" id="create-account">
      <div className="login-box">
        <div className="section-header">
          <h2>
            <span className="titleIcon"><FontAwesomeIcon icon={"clipboard-check"} /></span>&nbsp;&nbsp;
            Manage Dealership&nbsp;
            <span
              className={`status-text-title ${status.includes("Pending") ? "pending" : ""} ${status.includes("Approved") ? "approved" : ""} ${status.includes("Live") ? "live" : ""}`}
            >
              {status && `(${status})`}
            </span>
          </h2>
          <button
            onClick={() => toggleAllSections(!allExpanded)}
            className="expand-collapse-toggle"
          >
            {allExpanded ? "Collapse All ▲" : "Expand All ▼"}
          </button>
        </div>

        {claimId && <ManageDealershipSelector claimId={claimId} hasUnsavedChanges={hasUnsavedChanges}
          disabled={submitting || Boolean(switchingClaim)} onSwitch={setSwitchingClaim}
          currentName={businessName || fflName} />}

        <div ref={messageRef} className={messageClass} id="message" />

        <form ref={formRef} onSubmit={handleSubmit} className="claim-ffl-form">
          {/* ✅ Draft Auto-Save Message */}
          {lastSaved && (!claimId || hasUnsavedChanges) && (
            <div className="draft-saved-msg">
              🟢 Draft saved on this device. Select Save Changes to submit your updates.
            </div>
          )}

          <p className="section-note">Click any section below to expand or collapse it.</p>

          <p className="field-note"><span className="required">**</span> Denotes required fields</p>

          {canCustomizeSite && <div className="button-row" role="tablist" aria-label="Manage Dealership">
            <button type="button" role="tab" id="dealership-tab" aria-controls="dealership-panel" aria-selected={managementTab === "dealership"} className="claim-ffl-button" onClick={() => setManagementTab("dealership")}>Dealership Information</button>
{canCustomizeSite && <button type="button" role="tab" id="website-tab" aria-controls="website-panel" aria-selected={managementTab === "website"} aria-disabled={!canCustomizeSite} title={!canCustomizeSite ? "Website customization is available after claim approval." : undefined} className="claim-ffl-button" onClick={() => { if (canCustomizeSite) setManagementTab("website"); }}>Website Information</button>}
          </div>}
          <div id="dealership-panel" role={canCustomizeSite ? "tabpanel" : undefined} aria-labelledby={canCustomizeSite ? "dealership-tab" : undefined} hidden={canCustomizeSite && managementTab !== "dealership"}>
          <DealershipInfoPanel
            isOpen={panelStates.dealershipInfo}
            onToggle={() => setPanelStates(prev => ({
              ...prev,
              dealershipInfo: !prev.dealershipInfo
            }))}
            fflNumber={fflNumber}
            setFflNumber={setFflNumber}
            fflExpiration={expirationDate}
            fflName={fflName}
            setFflName={setFflName}
            businessName={businessName}
            setBusinessName={setBusinessName}
            phoneNumber={phoneNumber}
            optionalPhoneNumber={optionalPhoneNumber}
            setOptionalPhoneNumber={setOptionalPhoneNumber}
            streetAddress={streetAddress}
            cityStateZip={cityStateZip}
            expirationDate={expirationDate}
            email={email}
            setEmail={setEmail}
            website={website}
            setWebsite={setWebsite}
            updateLocalStorage={updateLocalStorage}
            handleFFLChange={handleFFLChange}
            fflInputRef={fflInputRef}
            fflNameRef={fflNameRef}
            fflBusinessNameRef={fflBusinessNameRef}
          />

          {/* --- FFL UPLOAD --- */}
          <FFLUploadPanel
            isOpen={panelStates.fflUpload}
            onToggle={() =>
              setPanelStates((prev) => ({ ...prev, fflUpload: !prev.fflUpload }))
            }
            afterExpand={() => {
              if (scrollToRef?.current) {
                scrollToRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                scrollToRef.current.focus?.();
                setScrollToRef(null); // Clear ref after scroll
              }
            }}
            file={file}
            filePreviewUrl={filePreviewUrl}
            handleFileChange={handleFileChange}
            handleDroppedFFL={handleDroppedFFL}
            openPhotoModal={openPhotoModal}
            validationErrors={validationErrors}
            fflFileRef={fflFileRef}
            fflDropInputRef={fflDropInputRef}
            isDragOverFile={isDragOverFile}
            setIsDragOverFile={setIsDragOverFile}
            setValidationErrors={setValidationErrors}
            status={status}
          />

          </div>
          {canCustomizeSite && <div id="website-panel" role="tabpanel" aria-labelledby="website-tab" hidden={managementTab !== "website"}>
          {trial.dialog}
          {pauseRequested && <WebsitePauseConfirmation onCancel={() => setPauseRequested(false)} onConfirm={() => { setPauseRequested(false); handleSiteStatus("Paused"); }} />}
          {canCustomizeSite && <WebsiteTrialDisclosure claimId={claimId} siteStatus={siteStatus} />}
          {/* --- SITE STATUS --- */}
          {canCustomizeSite && subdomain && !hideSiteStatusPanel && (
            <SiteStatusPanel
              isOpen={panelStates.siteStatus}
              onToggle={() =>
                setPanelStates(prev => ({ ...prev, siteStatus: !prev.siteStatus }))
              }
              siteStatus={siteStatus}
              handleSiteStatus={requestSiteStatus}
              disabled={siteStatusPending || pauseRequested}
              dealer={dealer}
            />
          )}

          {/* --- SUBDOMAIN --- */}
          {canCustomizeSite && <>
          <DealerSitePanel
            isOpen={panelStates.dealerSite}
            onToggle={() => setPanelStates(prev => ({
              ...prev,
              dealerSite: !prev.dealerSite
            }))}
            subdomain={subdomain}
            setSubdomain={setSubdomain}
            subdomainRef={subdomainRef}
            subdomainInputRef={subdomainInputRef}
            availability={availability}
            checkMessage={checkMessage}
            checking={checking}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            dealerURL={dealerURL}
            formData={formData}
            setFormData={setFormData}
            themes={themes}
            themeRef={themeRef}
            scrollToRef={scrollToRef}
            setScrollToRef={setScrollToRef}
            handleCheckboxChange={handleCheckboxChange}
            status={status}
            originalSubdomain={originalSubdomain}
          />

          {/* --- OVERVIEW --- */}
          <DealershipOverviewPanel
            isOpen={panelStates.dealerOverview}
            onToggle={() =>
              setPanelStates(prev => ({
                ...prev,
                dealerOverview: !prev.dealerOverview
              }))
            }
            formData={formData}
            setFormData={setFormData}
            handleGenerateAI={handleGenerateAI}
            loadingAI={loadingAI}
          />

          {/* --- SPECIALITIES --- */}
          <DealerSpecialtiesPanel
            isOpen={panelStates.dealerType}
            onToggle={() =>
              setPanelStates(prev => ({
                ...prev,
                dealerType: !prev.dealerType
              }))
            }
            formData={formData}
            setFormData={setFormData}
            customSpecialties={customSpecialties}
            setValidationErrors={setValidationErrors}
            validationErrors={validationErrors}
            specialtyRef={specialtyRef}
            updateSpecialty={updateSpecialty}
            removeSpecialty={removeSpecialty}
            addSpecialty={addSpecialty}
          />

          {/* --- SERVICES --- */}
          <DealerServicesPanel
            isOpen={panelStates.services}
            onToggle={() =>
              setPanelStates(prev => ({ ...prev, services: !prev.services }))
            }
            formData={formData}
            setFormData={setFormData}
            setValidationErrors={setValidationErrors}
            validationErrors={validationErrors}
            servicesRef={servicesRef}
          />

          {/* --- SOCIAL MEDIA --- */}
          <SocialMediaPanel
            isOpen={panelStates.socialMedia}
            onToggle={() =>
              setPanelStates(prev => ({ ...prev, socialMedia: !prev.socialMedia }))
            }
            formData={formData}
            setFormData={setFormData}
            normalizeURL={normalizeURL}
          />

          {/* --- TESTIMONIALS --- */}
          <CustomerTestimonialsPanel
            isOpen={panelStates.testimonials}
            onToggle={() =>
              setPanelStates(prev => ({ ...prev, testimonials: !prev.testimonials }))
            }
            testimonials={testimonials}
            updateTestimonial={updateTestimonial}
            removeTestimonial={removeTestimonial}
            addTestimonial={addTestimonial}
          />

          {/* --- MEDIA UPLOAD --- */}
          <MediaUploadPanel
            isOpen={panelStates.media}
            onToggle={() =>
              setPanelStates((prev) => ({ ...prev, media: !prev.media }))
            }
            logoFile={logoFile}
            setLogoFile={setLogoFile}
            logoPreview={logoPreview}
            setLogoPreview={setLogoPreview}
            setBannerCropSrc={setBannerCropSrc}
            logoDropInputRef={logoDropInputRef}
            isDragOverLogo={isDragOverLogo}
            setIsDragOverLogo={setIsDragOverLogo}
            handleDroppedLogo={handleDroppedLogo}

            formData={formData}
            setFormData={setFormData}
            bannerDropInputRef={bannerDropInputRef}
            isDragOverBanner={isDragOverBanner}
            setIsDragOverBanner={setIsDragOverBanner}
            handleDroppedBanner={handleDroppedBanner}

            photos={photos}
            setPhotos={setPhotos}
            fileInputRef={fileInputRef}
            isDragOverPhoto={isDragOverPhoto}
            setIsDragOverPhoto={setIsDragOverPhoto}
            handleDroppedPhotos={handleDroppedPhotos}
            handleDeletePhoto={handleDeletePhoto}
            setPhotoCropSrc={setPhotoCropSrc}
            setPhotoCropIndex={setPhotoCropIndex}
          />

          {/* --- HOURS --- */}

          <BusinessHoursPanel
            isOpen={panelStates.businessHours}
            onToggle={() =>
              setPanelStates((prev) => ({
                ...prev,
                businessHours: !prev.businessHours,
              }))
            }
            afterExpand={() => {
              if (scrollToRef?.current) {
                scrollToRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                scrollToRef.current.focus?.();
                setScrollToRef(null);
              }
            }}
            businesshoursRef={businesshoursRef}
            validationErrors={validationErrors}
            formData={formData}
            setFormData={setFormData}
            setValidationErrors={setValidationErrors}
          />

          {/* --- POLICY / FAQ SECTION --- */}
          <PoliciesPanel
            isOpen={panelStates.policies}
            onToggle={() =>
              setPanelStates((prev) => ({
                ...prev,
                policies: !prev.policies,
              }))
            }
            formData={formData}
            setFormData={setFormData}
          />

          </>}
          </div>}
          {/* Submit Button */}
          <div className="button-row" ref={bottomButtonsRef}>
            <button disabled={submitting} type="submit" className="claim-ffl-button">
              <FontAwesomeIcon icon="floppy-disk" /> {isFFLReadOnly && status ? "Save Changes" : "Submit FFL"}
            </button>
            {canCustomizeSite && isFFLReadOnly && status && (
              <button type="button" className="claim-ffl-button view-ffl-button" onClick={viewWebsite}>
                <FontAwesomeIcon icon="external-link-alt" />&nbsp;View My Website
              </button>
            )}
          </div>

        </form>

        {!bottomButtonsVisible && (
          <div className="button-row sticky-button-row">
            {/* 🚀 Sticky SAVE/UPDATE button */}
            <button
              type="button"
              disabled={submitting}
              className="sticky-save-btn"
              onClick={submitViaCode}
              style={!status ? { marginRight: '-80px' } : {}}
            >
              {isFFLReadOnly && status ? "Save" : "Submit"}
            </button>

            {/* 🚀 Sticky VIEW WEBSITE button */}
            {canCustomizeSite && (
              <button
                type="button"
                disabled={submitting}
                className="sticky-view-btn"
                onClick={viewWebsite}
              >
                View
              </button>
            )}
          </div>
        )}

      </div >

      {
        modalContentUrl && (
          <div className="modal-overlay" onClick={closePhotoModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              {modalContentType === "pdf" ? (
                <iframe
                  src={modalContentUrl}
                  title="FFL PDF Preview"
                  width="100%"
                  height="600px"
                  style={{ border: "none" }}
                />
              ) : (
                <img
                  src={modalContentUrl}
                  alt="Preview"
                  className="full-size-photo"
                />
              )}
              <button className="close-modal-btn" onClick={closePhotoModal}>
                &times;
              </button>
            </div>
          </div>
        )
      }

      {
        photoCropSrc && (
          <CropModal
            imageSrc={photoCropSrc}
            aspect={4 / 3}
            onClose={() => {
              setPhotoCropSrc(null);
              setPhotoCropIndex(null);
            }}
            onCropComplete={(croppedBlob) => {
              if (photos.length >= Config.APP.MAX_PHOTO_UPLOADS && photoCropIndex === null) {
                displayWarning(`Maximum of ${Config.APP.MAX_PHOTO_UPLOADS} photos allowed.`);
                setPhotoCropSrc(null);
                setPhotoCropIndex(null);
                return;
              }

              const previewUrl = URL.createObjectURL(croppedBlob);
              const file = new File([croppedBlob], `photo-${Date.now()}.jpg`, {
                type: "image/jpeg",
              });

              const newItem = {
                id: `photo-${Date.now()}`,
                name: file.name,
                file,
                url: previewUrl,
                isNew: true, // <-- mark new files
              };

              setPhotos((prev) => {
                if (photoCropIndex !== null && photoCropIndex !== undefined) {
                  const replacedPhoto = prev[photoCropIndex];
                  if (!replacedPhoto?.isNew && replacedPhoto?.serverFilename) {
                    setDeletedPhotos((prevDeleted) => [
                      ...prevDeleted,
                      replacedPhoto.serverFilename,
                    ]);
                  }

                  const updated = [...prev];
                  updated[photoCropIndex] = newItem;
                  return updated;
                }

                return [...prev, newItem];
              });

              setPhotoCropSrc(null);
              setPhotoCropIndex(null);
            }}
          />
        )
      }

      {
        bannerCropSrc && (
          <CropModal
            imageSrc={bannerCropSrc}
            aspect={21 / 9}
            onClose={() => setBannerCropSrc(null)}
            onCropComplete={(croppedBlob) => {
              const previewUrl = URL.createObjectURL(croppedBlob);
              setFormData(prev => ({
                ...prev,
                bannerFile: new File([croppedBlob], "banner.jpg", { type: "image/jpeg" }),
                bannerPreview: previewUrl,
                bannerFileName: "banner.jpg"
              }));
              setBannerCropSrc(null);
            }}
          />
        )
      }

      <Spinner show={submitting} />

      {
        showRenewModal && (
          <div className="modal-overlay">
            <div className="modal modal-wide">
              <h2>Upload Your FFL</h2>

              {/* --- FFL UPLOAD --- */}
              <DealerUploadFFL
                isOpen={panelStates.fflUpload}
                onToggle={() =>
                  setPanelStates((prev) => ({ ...prev, fflUpload: !prev.fflUpload }))
                }
                afterExpand={() => {
                  if (scrollToRef?.current) {
                    scrollToRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                    scrollToRef.current.focus?.();
                    setScrollToRef(null); // Clear ref after scroll
                  }
                }}
                file={file}
                filePreviewUrl={filePreviewUrl}
                handleFileChange={handleFileChange}
                handleDroppedFFL={handleDroppedFFL}
                openPhotoModal={openPhotoModal}
                validationErrors={validationErrors}
                fflFileRef={fflFileRef}
                fflDropInputRenewRef={fflDropInputRenewRef}
                isDragOverFile={isDragOverFile}
                setIsDragOverFile={setIsDragOverFile}
                setValidationErrors={setValidationErrors}
                status={status}
                claimId={claimId}
              />

              <div className="modal-buttons">
                <button className="confirm-delete confirm-delete-wide" onClick={closeUploadModal}>Cancel</button>
              </div>
            </div>
          </div>
        )
      }

    </div >
  );
};

// Claim changes must start with fresh editor state, including files, drafts and hydration.
function ClaimFFLRoute(props) {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const routedClaim = location.state?.claimId;
  const selectedClaim = routedClaim || localStorage.getItem("claimId") || "new";
  return <ClaimFFL key={`${user?.id || user?._id || ""}:${selectedClaim}`} {...props}
    fflData={routedClaim ? location.state : props.fflData} />;
}
export default ClaimFFLRoute;
